import type {
  DictEntry,
  DictResolveRequest,
  DictResolveResponse,
  DictSearchResponse,
  DictType,
} from "@adlywise/shared";

const API_BASE = import.meta.env?.VITE_API_URL ?? "/api";
const API_TOKEN = import.meta.env?.VITE_API_TOKEN;

const buildApiUrl = (path: string): string => {
  const base = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;

  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const getAuthToken = (): string | null => {
  if (typeof window === "undefined") {
    return API_TOKEN ?? null;
  }

  return window.localStorage.getItem("auth_token") ?? API_TOKEN ?? null;
};

export const searchDict = async (
  type: DictType,
  q: string,
  signal?: AbortSignal,
): Promise<DictEntry[]> => {
  const headers: HeadersInit = {};

  const token = getAuthToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const params = new URLSearchParams({
    type,
    q,
    limit: "20",
  });

  const requestInit: RequestInit = {
    method: "GET",
    headers,
    credentials: "include",
  };

  if (signal) {
    requestInit.signal = signal;
  }

  const response = await fetch(
    buildApiUrl(`/dictionaries/search?${params.toString()}`),
    requestInit,
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      message || `Request failed with status ${response.status.toString()}`,
    );
  }

  const data = (await response.json()) as DictSearchResponse;

  return data.results;
};

// Batch-resolve dict IDs (platforms/cities/regions) to {id,name} for display.
// Used by the aggregated-results table to turn group-column ids into names.
export const resolveDictIds = async (
  body: DictResolveRequest,
  signal?: AbortSignal,
): Promise<DictResolveResponse> => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestInit: RequestInit = {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "include",
  };
  if (signal) {
    requestInit.signal = signal;
  }

  const response = await fetch(
    buildApiUrl("/dictionaries/resolve"),
    requestInit,
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      message || `Request failed with status ${response.status.toString()}`,
    );
  }

  return (await response.json()) as DictResolveResponse;
};
